import { useQuery } from '@tanstack/react-query';

import { crmScreenerApiClient } from '@/adapters/schemaClients';
import { ALL_FACILITIES } from '@/hooks/useFacilitiesQuery';
import type { FacilityScreenerSettingsEntry } from '@/types/crm-screener-api';

import { SCREENER_SETTINGS_PER_FACILITY_KEY } from './useFacilityScreenerSettings';

type ScreenerSettingValue = string | number | object;

const SETTINGS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Settings for a single facility.
 *
 * `useFacilityScreenerSettings` seeds this cache slot for every facility it
 * batch-fetches, so the common case is a cache hit with no request. The slot
 * keeps its own `queryFn` so a miss can recover on its own: the batch query
 * lives in a `<ScreenerSettingsGuard>` that mounts once per page load and never
 * refetches, so without this a dropped slot stayed empty until a hard refresh
 * and any gated UI silently disappeared (PRIME-1233).
 *
 * `cacheTime: Infinity` keeps the slot alive while the user is on pages that
 * don't read it — stale settings still render the right UI, an evicted slot
 * renders nothing.
 *
 * A facility with no settings row caches `null` rather than `undefined`, which
 * react-query v4 rejects as a failed query; that would turn an absent setting
 * into an error state and refetch on every mount.
 */
export function useSingleFacilityScreenerSettingsQuery(facilityId: string) {
  return useQuery<FacilityScreenerSettingsEntry | null>({
    queryKey: [SCREENER_SETTINGS_PER_FACILITY_KEY, facilityId],
    queryFn: async () => {
      const response = await crmScreenerApiClient.getFacilityScreenerSettings({
        facilityIds: facilityId
      });
      return (
        (response.data ?? []).find(
          (entry) => entry.facility_id === facilityId
        ) ?? null
      );
    },
    enabled: !!facilityId && facilityId !== ALL_FACILITIES,
    staleTime: SETTINGS_STALE_TIME_MS,
    cacheTime: Infinity
  });
}

function useFacilityScreenerSettingsEntry(
  facilityId: string
): FacilityScreenerSettingsEntry | undefined {
  return useSingleFacilityScreenerSettingsQuery(facilityId).data ?? undefined;
}

export function useScreenerSetting<T extends ScreenerSettingValue = string>(
  settingName: string,
  facilityId: string
): T | undefined {
  return useFacilityScreenerSettingsEntry(facilityId)?.settings?.[
    settingName
  ] as T | undefined;
}

export function useIsScreenerSettingEnabled(
  settingName: string,
  facilityId: string
): boolean {
  return useScreenerSetting<string>(settingName, facilityId) === 'enabled';
}

export function useSingleFacilityScreenerSettings(
  facilityId: string
): FacilityScreenerSettingsEntry | undefined {
  return useFacilityScreenerSettingsEntry(facilityId);
}
